/**
 * Ventanas de **Finanzas → Transferencias**: verificar, ficha de la transferencia
 * y ficha del movimiento bancario con su edición.
 *
 * Son las tres pantallas del panel anterior —`verify.php` (verificar),
 * `view_movement.php` (ver el movimiento) y `edit_movement.php` (editarlo)—, más
 * el detalle de la venta que abría el listado.
 *
 * Reglas heredadas:
 * - El **importe no se escribe**: es el de la venta y sólo se muestra.
 * - La **fecha** del movimiento se propone con el día de la verificación.
 * - El **comprobante** es opcional al verificar; si se sube otro al editar, el
 *   anterior se borra del almacén.
 * - El detalle del movimiento guarda quién y cuándo lo verificó o lo actualizó,
 *   así que la ficha lo muestra tal como quedó.
 */

import { Ban, FileText, Paperclip } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { ArchivoInput } from '@/components/ui/archivo-input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CampoFecha } from '@/components/ui/calendario';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { cn, formatCurrency, formatDateValue } from '@/lib/utils';

import { transferenciasApi, useMovimientoTransferencia } from '../transferencias.api';
import type {
  CuentaTransferencia,
  MetodoTransferencia2,
  MovimientoTransferencia,
  MovimientoTransferenciaRespuesta,
  Transferencia,
  VentaTransferencia,
  VentaTransferenciaRespuesta,
} from '../transferencias.types';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const ACEPTADOS = 'application/pdf,image/png,image/jpeg';

/** Lo que devuelven los dos formularios. */
export interface DatosTransferencia {
  cuenta_id: string;
  metodo_pago_id: string;
  fecha: string;
  referencia: string;
  notas: string;
  comprobante: File | null;
}

/** Un dato de la ficha, con su etiqueta. */
function Dato({ etiqueta, valor }: { etiqueta: string; valor: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
        {etiqueta}
      </span>
      <span className="text-sm break-words text-ink-800">{valor}</span>
    </div>
  );
}

/** Los datos de la venta, que se repiten en la verificación y en la ficha. */
function DatosDeLaVenta({ venta }: { venta: VentaTransferencia }) {
  return (
    <section className="rounded-card border border-ink-100 p-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Dato etiqueta="Documento" valor={venta.documento ?? '—'} />
        <Dato etiqueta="Tipo" valor={<Badge variant="brand">{venta.tipo_etiqueta}</Badge>} />
        <Dato
          etiqueta="Turno"
          valor={venta.turno ? `#${venta.turno}` : venta.turno_id ? `#${venta.turno_id}` : '—'}
        />
        <Dato etiqueta="Fecha" valor={formatDateValue(venta.fecha_venta)} />
        <Dato etiqueta="Hora" valor={venta.hora ?? '—'} />
        <Dato
          etiqueta="Monto de la transferencia"
          valor={
            <span className="font-bold text-emerald-700 tabular-nums">
              {formatCurrency(venta.monto)}
            </span>
          }
        />
        <div className="sm:col-span-2 lg:col-span-3">
          <Dato
            etiqueta="Cliente"
            valor={
              <>
                {venta.cliente}
                {venta.cliente_documento ? (
                  <span className="text-ink-500"> · {venta.cliente_documento}</span>
                ) : null}
              </>
            }
          />
        </div>
      </div>
    </section>
  );
}

/** Cuenta, método, fecha, referencia, notas y comprobante. */
function CamposDelMovimiento({
  id,
  datos,
  cuentas,
  metodos,
  onCambiar,
  notaArchivo,
  nombreActual,
  etiquetaReferencia = 'Referencia (opcional)',
}: {
  id: string;
  datos: DatosTransferencia;
  cuentas: CuentaTransferencia[];
  metodos: MetodoTransferencia2[];
  onCambiar: (datos: DatosTransferencia) => void;
  notaArchivo: string;
  nombreActual?: string | null;
  etiquetaReferencia?: string;
}) {
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink-700" htmlFor={`${id}-cuenta`}>
            Cuenta bancaria
          </label>
          <Select
            id={`${id}-cuenta`}
            value={datos.cuenta_id}
            onChange={(evento) => onCambiar({ ...datos, cuenta_id: evento.target.value })}
            className={CONTROL}
            aria-label="Cuenta bancaria"
          >
            <option value="">Seleccionar…</option>
            {cuentas.map((cuenta) => (
              <option key={cuenta.id} value={String(cuenta.id)}>
                {cuenta.etiqueta}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink-700" htmlFor={`${id}-metodo`}>
            Método de pago
          </label>
          <Select
            id={`${id}-metodo`}
            value={datos.metodo_pago_id}
            onChange={(evento) => onCambiar({ ...datos, metodo_pago_id: evento.target.value })}
            className={CONTROL}
            aria-label="Método de pago"
          >
            <option value="">Seleccionar…</option>
            {metodos.map((metodo) => (
              <option key={metodo.id} value={String(metodo.id)}>
                {metodo.nombre}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink-700" htmlFor={`${id}-fecha`}>
            Fecha del movimiento
          </label>
          <CampoFecha
            id={`${id}-fecha`}
            tipo="date"
            value={datos.fecha}
            onChange={(evento) => onCambiar({ ...datos, fecha: evento.target.value })}
            aria-label="Fecha del movimiento"
          />
        </div>

        <Input
          label={etiquetaReferencia}
          value={datos.referencia}
          placeholder="Nro. de operación del banco"
          onChange={(evento) => onCambiar({ ...datos, referencia: evento.target.value })}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-ink-700" htmlFor={`${id}-notas`}>
          Observaciones (opcional)
        </label>
        <textarea
          id={`${id}-notas`}
          rows={2}
          maxLength={500}
          className={cn(CONTROL, 'h-auto py-2')}
          value={datos.notas}
          onChange={(evento) => onCambiar({ ...datos, notas: evento.target.value })}
        />
      </div>

      <ArchivoInput
        label="Comprobante (opcional)"
        accept={ACEPTADOS}
        nombre={datos.comprobante?.name ?? null}
        hint={notaArchivo}
        onSeleccionar={(elegido) => onCambiar({ ...datos, comprobante: elegido?.archivo ?? null })}
      />
      {nombreActual ? (
        <p className="text-xs text-ink-500">
          Comprobante actual: {nombreActual}
          {datos.comprobante ? ' (se reemplazará)' : ''}
        </p>
      ) : null}
    </>
  );
}

interface DialogoVerificarProps {
  transferencia: Transferencia | null;
  venta: VentaTransferenciaRespuesta | undefined;
  cargando: boolean;
  hoy: string;
  guardando: boolean;
  error: string | null;
  onCerrar: () => void;
  onVerificar: (datos: DatosTransferencia) => void;
}

/** Verificación de una transferencia contra el banco. */
export function DialogoVerificarTransferencia({
  transferencia,
  venta,
  cargando,
  hoy,
  guardando,
  error,
  onCerrar,
  onVerificar,
}: DialogoVerificarProps) {
  // El formulario se monta al abrirse la ventana, así que arranca limpio.
  const [datos, setDatos] = useState<DatosTransferencia>(() => ({
    cuenta_id: '',
    metodo_pago_id: '',
    fecha: hoy,
    referencia: '',
    notas: '',
    comprobante: null,
  }));

  const puedeGuardar = Boolean(datos.cuenta_id && datos.metodo_pago_id && datos.fecha) && !guardando;

  return (
    <Dialog
      open={Boolean(transferencia)}
      onClose={onCerrar}
      title="Verificar transferencia"
      description={
        transferencia
          ? `${transferencia.tipo_venta_etiqueta} ${transferencia.documento ?? ''} · turno ${
              transferencia.turno ?? transferencia.turno_id ?? '—'
            }`
          : undefined
      }
      size="lg"
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!puedeGuardar}
            onClick={() => onVerificar(datos)}
          >
            Verificar transferencia
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? (
          <Alert variant="error" title="No se pudo verificar">
            {error}
          </Alert>
        ) : null}

        {cargando ? (
          <div className="space-y-2">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : venta ? (
          <DatosDeLaVenta venta={venta.venta} />
        ) : null}

        <p className="text-xs text-ink-500">
          El importe es el de la venta y no se modifica. Al verificar se crea el movimiento bancario
          de la cuenta elegida y la venta queda marcada con quién la verificó y cuándo.
        </p>

        <CamposDelMovimiento
          id="verificar"
          datos={datos}
          cuentas={venta?.cuentas ?? []}
          metodos={venta?.metodos_pago ?? []}
          onCambiar={setDatos}
          notaArchivo="PDF, PNG, JPG o JPEG de hasta 5 MB. Opcional: si falta, el movimiento queda sin comprobante."
        />
      </div>
    </Dialog>
  );
}

interface DialogoDetalleProps {
  transferencia: Transferencia | null;
  venta: VentaTransferenciaRespuesta | undefined;
  cargando: boolean;
  onCerrar: () => void;
}

/** Ficha de la transferencia y de la venta a la que pertenece. */
export function DialogoDetalleTransferencia({
  transferencia,
  venta,
  cargando,
  onCerrar,
}: DialogoDetalleProps) {
  const [error, setError] = useState<string | null>(null);
  const movimientoId = venta?.venta.movement_id ?? transferencia?.movement_id ?? null;
  // El comprobante vive en el movimiento, así que sólo se ofrece si existe.
  const ficha = useMovimientoTransferencia(movimientoId);
  const tieneComprobante = Boolean(ficha.data?.movimiento.comprobante?.disponible);

  const abrirComprobante = async () => {
    if (!movimientoId) return;

    setError(null);

    try {
      await abrirPdfAutenticado(transferenciasApi.urlComprobante(movimientoId));
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo abrir el comprobante.');
    }
  };

  return (
    <Dialog
      open={Boolean(transferencia)}
      onClose={onCerrar}
      title="Detalle de la transferencia"
      description={
        transferencia
          ? `${transferencia.tipo_venta_etiqueta} ${transferencia.documento ?? ''} · método ${
              transferencia.tipo_metodo
            }`
          : undefined
      }
      size="lg"
      footer={
        <div className="flex w-full flex-wrap items-center justify-end gap-2">
          {tieneComprobante ? (
            <Button
              variant="outline"
              size="sm"
              leftIcon={<Paperclip className="size-3.5" aria-hidden="true" />}
              onClick={() => void abrirComprobante()}
            >
              Ver comprobante
            </Button>
          ) : null}

          <Button variant="outline" size="sm" onClick={onCerrar}>
            Cerrar
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error ? (
          <Alert variant="error" title="No se pudo abrir el comprobante">
            {error}
          </Alert>
        ) : null}

        {cargando ? (
          <div className="space-y-2">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : venta ? (
          <>
            <DatosDeLaVenta venta={venta.venta} />

            <section className="rounded-card border border-ink-100 p-4">
              <h3 className="text-sm font-semibold text-ink-800">Estado de la verificación</h3>

              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <Dato
                  etiqueta="Estado"
                  valor={
                    venta.venta.verificado ? (
                      <Badge variant="success">Verificada</Badge>
                    ) : (
                      <Badge variant="warning">Pendiente</Badge>
                    )
                  }
                />
                <Dato
                  etiqueta="Movimiento bancario"
                  valor={venta.venta.movement_id ? `#${venta.venta.movement_id}` : '—'}
                />
                <Dato etiqueta="Verificó" valor={venta.venta.verificado_por ?? '—'} />
                <Dato etiqueta="Fecha de verificación" valor={venta.venta.verificado_el ?? '—'} />
                <Dato
                  etiqueta="Método original de la venta"
                  valor={venta.venta.metodo_pago_original ?? '—'}
                />
              </div>
            </section>
          </>
        ) : (
          <p className="text-sm text-ink-500">No se han podido leer los datos de la venta.</p>
        )}
      </div>
    </Dialog>
  );
}

interface FormularioMovimientoProps {
  movimiento: MovimientoTransferencia;
  cuentas: CuentaTransferencia[];
  metodos: MetodoTransferencia2[];
  guardando: boolean;
  onGuardar: (datos: DatosTransferencia) => void;
}

/** Formulario de edición del movimiento: se monta con lo que ya tiene guardado. */
function FormularioMovimiento({
  movimiento,
  cuentas,
  metodos,
  guardando,
  onGuardar,
}: FormularioMovimientoProps) {
  const [datos, setDatos] = useState<DatosTransferencia>(() => ({
    cuenta_id: movimiento.cuenta_id ? String(movimiento.cuenta_id) : '',
    metodo_pago_id: movimiento.metodo_pago_id ? String(movimiento.metodo_pago_id) : '',
    fecha: movimiento.fecha ?? '',
    referencia: movimiento.referencia ?? '',
    notas: movimiento.observaciones ?? '',
    comprobante: null,
  }));

  const puedeGuardar = Boolean(datos.cuenta_id && datos.metodo_pago_id && datos.fecha) && !guardando;

  return (
    <section className="rounded-card border border-brand-100 bg-brand-50/40 p-4">
      <h3 className="mb-3 text-sm font-semibold text-ink-800">Editar el movimiento</h3>

      <div className="space-y-4">
        <CamposDelMovimiento
          id="movimiento"
          datos={datos}
          cuentas={cuentas}
          metodos={metodos}
          onCambiar={setDatos}
          notaArchivo="Si se sube uno nuevo, el comprobante anterior se borra del almacén."
          nombreActual={movimiento.comprobante?.archivo ?? null}
          etiquetaReferencia="Referencia"
        />

        <div className="flex flex-wrap justify-end gap-2">
          <Button
            size="sm"
            loading={guardando}
            disabled={!puedeGuardar}
            onClick={() => onGuardar(datos)}
          >
            Guardar cambios
          </Button>
        </div>
      </div>
    </section>
  );
}

interface DialogoMovimientoProps {
  /** Movimiento abierto; si es `null`, la ventana está cerrada. */
  movimientoId: number | null;
  consulta: MovimientoTransferenciaRespuesta | undefined;
  cargando: boolean;
  cuentas: CuentaTransferencia[];
  metodos: MetodoTransferencia2[];
  guardando: boolean;
  error: string | null;
  /** Si la ventana se abre pulsando «editar», el formulario ya está desplegado. */
  abrirEditando: boolean;
  onCerrar: () => void;
  onGuardar: (id: number, datos: DatosTransferencia) => void;
  onAnular: (movimiento: MovimientoTransferencia) => void;
}

/** Ficha del movimiento bancario de una transferencia, con su edición. */
export function DialogoMovimientoTransferencia({
  movimientoId,
  consulta,
  cargando,
  cuentas,
  metodos,
  guardando,
  error,
  abrirEditando,
  onCerrar,
  onGuardar,
  onAnular,
}: DialogoMovimientoProps) {
  const movimiento = consulta?.movimiento;
  const [editando, setEditando] = useState(abrirEditando);
  const [errorComprobante, setErrorComprobante] = useState<string | null>(null);

  const abrirComprobante = async () => {
    if (!movimientoId) return;

    setErrorComprobante(null);

    try {
      await abrirPdfAutenticado(transferenciasApi.urlComprobante(movimientoId));
    } catch (fallo) {
      setErrorComprobante(fallo instanceof Error ? fallo.message : 'No se pudo abrir el comprobante.');
    }
  };

  const detalle = movimiento?.detalle ?? {};

  return (
    <Dialog
      open={Boolean(movimientoId)}
      onClose={onCerrar}
      title={movimientoId ? `Movimiento #${movimientoId}` : 'Movimiento'}
      description={
        movimiento ? `${movimiento.tipo} · ${movimiento.banco} · ${movimiento.cuenta}` : undefined
      }
      size="lg"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div>
            {movimiento && !movimiento.anulado ? (
              <Button
                variant="danger"
                size="sm"
                leftIcon={<Ban className="size-3.5" aria-hidden="true" />}
                disabled={guardando}
                onClick={() => onAnular(movimiento)}
              >
                Anular y liberar
              </Button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {movimiento?.comprobante?.disponible ? (
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Paperclip className="size-3.5" aria-hidden="true" />}
                onClick={() => void abrirComprobante()}
              >
                Ver comprobante
              </Button>
            ) : null}

            {editando ? (
              <Button variant="outline" size="sm" onClick={() => setEditando(false)} disabled={guardando}>
                Cancelar
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!movimiento || movimiento.anulado}
                  onClick={() => setEditando(true)}
                >
                  Editar
                </Button>
                <Button variant="outline" size="sm" onClick={onCerrar}>
                  Cerrar
                </Button>
              </>
            )}
          </div>
        </div>
      }
    >
      {cargando ? (
        <div className="space-y-2">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <div className="space-y-4">
          {error ? (
            <Alert variant="error" title="No se pudo completar la acción">
              {error}
            </Alert>
          ) : null}

          {errorComprobante ? (
            <Alert variant="error" title="No se pudo abrir el comprobante">
              {errorComprobante}
            </Alert>
          ) : null}

          {movimiento?.anulado ? (
            <Alert variant="warning" title="Movimiento anulado">
              Lo anuló {movimiento.anulado_por ?? '—'}
              {movimiento.anulado_el ? ` el ${movimiento.anulado_el}` : ''}. La venta volvió a quedar
              pendiente de verificar.
            </Alert>
          ) : null}

          {movimiento ? (
            <>
              <section className="rounded-card border border-ink-100 p-4">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <Dato etiqueta="Estado" valor={<Badge variant="brand">{movimiento.estado}</Badge>} />
                  <Dato etiqueta="Fecha del movimiento" valor={formatDateValue(movimiento.fecha)} />
                  <Dato
                    etiqueta="Monto"
                    valor={
                      <span className="font-bold text-emerald-700 tabular-nums">
                        {formatCurrency(movimiento.monto)}
                      </span>
                    }
                  />
                  <Dato etiqueta="Banco" valor={movimiento.banco} />
                  <Dato
                    etiqueta="Cuenta"
                    valor={
                      movimiento.cuenta_numero
                        ? `${movimiento.cuenta} · ${movimiento.cuenta_numero}`
                        : movimiento.cuenta
                    }
                  />
                  <Dato etiqueta="Método de pago" valor={movimiento.metodo_pago} />
                  <Dato etiqueta="Referencia" valor={movimiento.referencia ?? '—'} />
                  <Dato etiqueta="Registrado" valor={movimiento.registrado ?? '—'} />
                  <Dato etiqueta="Registró" valor={movimiento.ejecutor} />
                  <Dato etiqueta="Verificado por" valor={detalle.verified_by ?? '—'} />
                  <Dato etiqueta="Verificado el" valor={detalle.verified_at ?? '—'} />
                  {detalle.updated_by ? (
                    <Dato
                      etiqueta="Actualizado"
                      valor={`${detalle.updated_by} · ${detalle.updated_at ?? ''}`}
                    />
                  ) : null}
                </div>
              </section>

              <section className="rounded-card border border-ink-100 p-4">
                <h3 className="text-sm font-semibold text-ink-800">Venta de origen</h3>
                <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <Dato etiqueta="Documento" valor={detalle.document_number ?? '—'} />
                  <Dato etiqueta="Cliente" valor={detalle.client_name ?? '—'} />
                  <Dato etiqueta="Documento del cliente" valor={detalle.client_document || '—'} />
                  <Dato etiqueta="Fecha de la venta" valor={formatDateValue(detalle.sale_date)} />
                  <Dato etiqueta="Turno" valor={detalle.shift_id ? `#${detalle.shift_id}` : '—'} />
                  <Dato
                    etiqueta="Importe de la venta"
                    valor={formatCurrency(detalle.sale_amount ?? movimiento.monto)}
                  />
                </div>
              </section>

              {movimiento.observaciones ? (
                <section className="rounded-card border border-ink-100 p-4">
                  <h3 className="text-sm font-semibold text-ink-800">Observaciones</h3>
                  <p className="mt-1 flex items-start gap-2 text-sm text-ink-600">
                    <FileText className="mt-0.5 size-3.5 shrink-0 text-ink-400" aria-hidden="true" />
                    <span>{movimiento.observaciones}</span>
                  </p>
                </section>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-ink-500">No se ha podido leer el movimiento.</p>
          )}

          {editando && movimiento ? (
            <FormularioMovimiento
              // Al cambiar de movimiento, el formulario se vuelve a montar con sus datos.
              key={movimiento.id}
              movimiento={movimiento}
              cuentas={cuentas}
              metodos={metodos}
              guardando={guardando}
              onGuardar={(datos) => onGuardar(movimiento.id, datos)}
            />
          ) : null}
        </div>
      )}
    </Dialog>
  );
}
